import { noop } from "@tanstack/react-table";
import React, { useCallback, useState } from "react";
import type { ColumnDefinition, DataRow } from "../schema/chart";
import { DataTable } from "./charts/DataTable";
import { parseTSV } from "./charts/parseTSV";

// ---------------------------------------------------------------------------
// Type detection helpers
// ---------------------------------------------------------------------------

export function isCSVFile(type?: string | null, name?: string | null): boolean {
    if (type === "text/csv" || type === "text/tab-separated-values") return true;
    if (name) {
        const lower = name.toLowerCase();
        if (lower.endsWith(".csv") || lower.endsWith(".tsv")) return true;
    }
    // text/plain files with csv/tsv extension
    if (type === "text/plain" && name) {
        const lower = name.toLowerCase();
        return lower.endsWith(".csv") || lower.endsWith(".tsv");
    }
    return false;
}

export function isPDFFile(type?: string | null, name?: string | null): boolean {
    if (type === "application/pdf") return true;
    if (name?.toLowerCase().endsWith(".pdf")) return true;
    return false;
}

export function isPreviewableFile(type?: string | null, name?: string | null): boolean {
    return isCSVFile(type, name) || isPDFFile(type, name);
}

// ---------------------------------------------------------------------------
// CSV Preview — uses the same DataTable that renders charts in threads
// ---------------------------------------------------------------------------

const CSVPreview: React.FC<{
    fetchContent: () => Promise<string>;
    maxRows: number;
    fileName?: string | null;
}> = ({ fetchContent, maxRows, fileName }) => {
    const [state, setState] = useState<
        | { status: "idle" }
        | { status: "loading" }
        | { status: "error"; message: string }
        | { status: "loaded"; columns: ColumnDefinition[]; allRows: DataRow[]; totalRows: number }
    >({ status: "idle" });
    const [showAll, setShowAll] = useState(false);

    const load = useCallback(async () => {
        if (state.status === "loading" || state.status === "loaded") return;
        setState({ status: "loading" });
        try {
            const text = await fetchContent();
            const parsed = parseTSV(text);
            if (!parsed || parsed.columns.length === 0) {
                setState({ status: "error", message: "Could not parse file" });
                return;
            }
            setState({
                status: "loaded",
                columns: parsed.columns,
                allRows: parsed.rows,
                totalRows: parsed.rows.length,
            });
        } catch {
            setState({ status: "error", message: "Failed to load file" });
        }
    }, [fetchContent, maxRows, state.status]);

    if (state.status === "idle") {
        return (
            <button
                onClick={load}
                className="mt-1 flex items-center gap-1 text-xs text-blue-600 hover:underline"
            >
                Preview spreadsheet
            </button>
        );
    }

    if (state.status === "loading") {
        return <span className="mt-1 text-xs text-slate-500">Loading preview...</span>;
    }

    if (state.status === "error") {
        return <span className="mt-1 text-xs text-red-500">{state.message}</span>;
    }

    const { columns, allRows, totalRows } = state;
    const rows = showAll ? allRows : allRows.slice(0, maxRows);
    const hasMore = totalRows > maxRows;
    const exportName = fileName?.replace(/\.[^.]+$/, "") ?? "preview";

    return (
        <div className="relative mt-1 rounded border border-slate-200 bg-white">
            <button
                className="absolute top-1 right-1 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 shadow-sm hover:bg-slate-100"
                onClick={() => setState({ status: "idle" })}
                title="Close preview"
            >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                    <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                    />
                </svg>
            </button>
            <DataTable
                columns={columns}
                rows={rows}
                onChange={noop}
                readOnly
                exportFilename={exportName}
            />
            {hasMore && (
                <p className="p-2 text-center text-xs text-slate-400">
                    {showAll ? (
                        <>
                            Showing all {totalRows} rows{" "}
                            <button
                                onClick={() => setShowAll(false)}
                                className="text-blue-600 hover:underline"
                            >
                                Show less
                            </button>
                        </>
                    ) : (
                        <>
                            Showing {rows.length} of {totalRows} rows{" "}
                            <button
                                onClick={() => setShowAll(true)}
                                className="text-blue-600 hover:underline"
                            >
                                Show all
                            </button>
                        </>
                    )}
                </p>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export type FileContentPreviewProps = {
    fileType?: string | null;
    fileName?: string | null;
    /** Fetch text content (for CSV/TSV) */
    fetchContent: () => Promise<string>;
    /** Fetch a URL for binary content (for PDF) */
    fetchUrl: () => Promise<string>;
    /** Maximum rows to show in CSV preview. Default 50 */
    maxPreviewRows?: number;
    /** Optional custom PDF renderer. Receives the URL when loaded. */
    renderPDF?: (url: string) => React.ReactNode;
};

export const FileContentPreview: React.FC<FileContentPreviewProps> = ({
    fileType,
    fileName,
    fetchContent,
    fetchUrl,
    maxPreviewRows = 50,
    renderPDF,
}) => {
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState<string | null>(null);

    if (isCSVFile(fileType, fileName)) {
        return (
            <CSVPreview fetchContent={fetchContent} maxRows={maxPreviewRows} fileName={fileName} />
        );
    }

    if (isPDFFile(fileType, fileName)) {
        if (!renderPDF) {
            // No PDF renderer provided — show a simple "open" link
            return (
                <button
                    onClick={async () => {
                        const url = await fetchUrl();
                        window.open(url, "_blank");
                    }}
                    className="mt-1 flex items-center gap-1 text-xs text-blue-600 hover:underline"
                >
                    View PDF
                </button>
            );
        }

        if (!pdfUrl) {
            return (
                <button
                    onClick={async () => {
                        if (pdfLoading) return;
                        setPdfLoading(true);
                        try {
                            const url = await fetchUrl();
                            setPdfUrl(url);
                        } catch {
                            setPdfError("Failed to load PDF");
                        } finally {
                            setPdfLoading(false);
                        }
                    }}
                    className="mt-1 flex items-center gap-1 text-xs text-blue-600 hover:underline"
                >
                    {pdfLoading ? "Loading..." : pdfError || "Preview PDF"}
                </button>
            );
        }

        return (
            <div className="relative mt-1 max-h-96 overflow-auto rounded border border-slate-200 bg-white">
                <button
                    className="sticky top-1 right-1 z-10 float-right mr-1 mt-1 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 shadow-sm hover:bg-slate-100"
                    onClick={() => setPdfUrl(null)}
                    title="Close preview"
                >
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                        <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                        />
                    </svg>
                </button>
                {renderPDF(pdfUrl)}
            </div>
        );
    }

    return null;
};
